<template>
  <section class="profile-page">
    <div class="profile-card">
      <h2>My Profile</h2>

      <p v-if="success" class="success">
        Profile updated successfully
      </p>

      <form @submit.prevent="saveProfile">

        <!-- Full Name -->
        <div class="form-group">
          <label>Full Name</label>
          <input
            v-model="form.fullname"
            type="text"
            placeholder="Enter your full name"
          />
        </div>

        <!-- Email -->
        <div class="form-group">
          <label>Email</label>
          <input
            v-model="form.email"
            type="email"
            placeholder="Enter your email"
          />
        </div>

        <!-- Company -->
        <div class="form-group">
          <label>Company</label>
          <input
            v-model="form.company"
            type="text"
            placeholder="Enter company name"
          />
        </div>

        <div class="divider"></div>

       <p class="password-note">
         Leave blank to keep current password
       </p>
        <!-- Password -->
        <div class="form-group">
          <label>New Password</label>

          <div class="password-wrapper">
            <input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              placeholder="Enter new password"
            />

            <button
              type="button"
              class="eye-btn"
              @click="showPassword = !showPassword"
            >
    <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>

          <!-- Password Strength -->
          <div
            v-if="form.password"
            class="password-strength"
          >
            <div class="strength-bar">
              <div
                class="strength-fill"
                :class="strengthClass"
                :style="{ width: strengthWidth }"
              ></div>
            </div>

            <small :class="strengthClass">
              {{ passwordStrength }}
            </small>
          </div>

          <!-- Password Requirements -->
          <ul v-if="form.password" class="requirements">
            <li :class="{ valid: validations.length }">
              8+ characters
            </li>

            <li :class="{ valid: validations.uppercase }">
              Uppercase letter
            </li>

            <li :class="{ valid: validations.lowercase }">
              Lowercase letter
            </li>

            <li :class="{ valid: validations.number }">
              Number
            </li>

            <li :class="{ valid: validations.special }">
              Special character
            </li>
          </ul>
        </div>

        <!-- Confirm Password -->
        <div class="form-group">
          <label>Confirm New Password</label>

          <div class="password-wrapper">
            <input
              v-model="form.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              autocomplete="new-password"
              placeholder="Confirm new password"
            />

            <button
              type="button"
              class="eye-btn"
              @click="showConfirmPassword = !showConfirmPassword"
            >
    <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
        </div>

        <p v-if="passwordError" class="error">
          {{ passwordError }}
        </p>

        <button type="submit" :disabled="loading">
          {{ loading ? "Saving..." : "Save Changes" }}
        </button>
      </form>
    </div>
  </section>
</template>

<script setup>
import { reactive, ref, computed } from "vue";
import { useMutation } from "@vue/apollo-composable";
import { UPDATE_USER } from "@/hooks/queries";

const success = ref(false);
const passwordError = ref("");

const showPassword = ref(false);
const showConfirmPassword = ref(false);

/*
|--------------------------------------------------------------------------
| Load user
|--------------------------------------------------------------------------
*/

const userString = localStorage.getItem("user");
const user = userString ? JSON.parse(userString) : null;

/*
|--------------------------------------------------------------------------
| Form State
|--------------------------------------------------------------------------
*/

const form = reactive({
  fullname: user?.fullname || "",
  email: user?.email || "",
  company: user?.company || "",
  password: "",
  confirmPassword: "",
});

/*
|--------------------------------------------------------------------------
| Password Validation
|--------------------------------------------------------------------------
*/

const validations = computed(() => ({
  length: form.password.length >= 8,
  uppercase: /[A-Z]/.test(form.password),
  lowercase: /[a-z]/.test(form.password),
  number: /\d/.test(form.password),
  special: /[!@#$%^&*(),.?":{}|<>-]/.test(form.password),
}));

const passwordScore = computed(() => {
  return Object.values(validations.value).filter(Boolean).length;
});

const passwordStrength = computed(() => {
  const score = passwordScore.value;

  if (score <= 2) return "Weak Password";
  if (score <= 4) return "Medium Password";

  return "Strong Password";
});

const strengthClass = computed(() => {
  const score = passwordScore.value;

  if (score <= 2) return "weak";
  if (score <= 4) return "medium";

  return "strong";
});

const strengthWidth = computed(() => {
  return `${(passwordScore.value / 5) * 100}%`;
});

/*
|--------------------------------------------------------------------------
| GraphQL Mutation
|--------------------------------------------------------------------------
*/

const { mutate, loading } = useMutation(UPDATE_USER);

/*
|--------------------------------------------------------------------------
| Save Profile
|--------------------------------------------------------------------------
*/

async function saveProfile() {
  success.value = false;
  passwordError.value = "";

  /*
  |--------------------------------------------------------------------------
  | Password Validation
  |--------------------------------------------------------------------------
  */

  if (form.password || form.confirmPassword) {

    if (passwordScore.value < 4) {
      passwordError.value =
        "Please choose a stronger password";
      return;
    }

    if (form.password !== form.confirmPassword) {
      passwordError.value = "Passwords do not match";
      return;
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Payload
  |--------------------------------------------------------------------------
  */

  const userData = {
    fullname: form.fullname.trim(),
    email: form.email.trim(),
    username: form.email.trim(),
    company: form.company.trim(),
  };

  if (form.password) {
    userData.password = form.password;
  }

  try {
    const response = await mutate({
      documentId: user.documentId,
      data: userData,
    });

    if (response) {
      // console.log(response.data);
    }

    /*
    |--------------------------------------------------------------------------
    | Update localStorage
    |--------------------------------------------------------------------------
    */

    localStorage.setItem(
      "user",
      JSON.stringify({
        ...user,
        fullname: form.fullname,
        email: form.email,
        company: form.company,
      })
    );

    form.password = "";
    form.confirmPassword = "";

    success.value = true;

  } catch (err) {
    console.error(err);

    passwordError.value =
      err.message || "Failed to update profile";
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  padding: 40px 20px;
  background: #f5f7fb;
}

.profile-card {
  max-width: 650px;
  margin: auto;
  background: #fff;
  padding: 35px;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.06);
}

h2 {
  margin-bottom: 25px;
  font-size: 28px;
  font-weight: 700;
}

h3 {
  margin-bottom: 10px;
  color: #333;
}

form {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

label {
  font-size: 14px;
  font-weight: 600;
  color: #444;
}

input {
  width: 100%;
  padding: 14px;
  border: 1px solid #d7dce5;
  border-radius: 12px;
  font-size: 15px;
  transition: 0.2s;
  background: #fff;
}

input:focus {
  outline: none;
  border-color: #4f7cff;
  box-shadow: 0 0 0 4px rgba(79,124,255,0.12);
}

.password-wrapper {
  position: relative;
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: #6b7280;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.eye-btn:hover {
  color: #111827;
}

.password-strength {
  margin-top: 10px;
}

.strength-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: 0.3s;
}

.strength-fill.weak {
  background: #ef4444;
}

.strength-fill.medium {
  background: #f59e0b;
}

.strength-fill.strong {
  background: #22c55e;
}

small.weak {
  color: #ef4444;
}

small.medium {
  color: #f59e0b;
}

small.strong {
  color: #22c55e;
}

.requirements {
  margin-top: 10px;
  padding-left: 18px;
  font-size: 13px;
  color: #888;
}

.requirements li {
  margin-bottom: 4px;
}

.requirements li.valid {
  color: #22c55e;
  font-weight: 600;
}

button[type="submit"] {
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #4f7cff;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
}

button[type="submit"]:hover {
  background: #3e68df;
}

button[type="submit"]:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.success {
  padding: 12px;
  border-radius: 10px;
  background: #dcfce7;
  color: #166534;
  margin-bottom: 20px;
}

.error {
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
}

.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 10px 0;
}
.password-note {
  margin-top: -5px;
  color: #6b7280;
  font-size: 14px;
}	      
</style>
